import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { MapPin, ArrowUp, Mic, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useGeo } from "@/context/GeoContext";
import { mapLocaleToBcp47 } from "@/lib/speechLocale";
import safeStorage from "@/lib/safeStorage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Detect Web Speech API once at module load. Firefox without flags exposes
// nothing; we render no mic button in that case (silent degradation).
const SR =
    typeof window !== "undefined"
        ? window.SpeechRecognition || window.webkitSpeechRecognition
        : null;

const VOICE_TIP_KEY = "tc_voice_tip_dismissed";
const CONVERSATION_MODE_KEY = "tc_conversation_mode";

// Safari's webkitSpeechRecognition rejects Dravidian Indic locales — those
// route through our `/api/speech/transcribe` server fallback (Phase 8I).
const SAFARI_FALLBACK_LOCALES = new Set(["ta-IN", "te-IN", "ml-IN", "kn-IN"]);
function isSafariBrowser() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
}
function shouldUseServerTranscribe(bcp47) {
    return isSafariBrowser() && SAFARI_FALLBACK_LOCALES.has(bcp47);
}

// Module-scoped — anchored on `window` so an HMR reload or composer
// remount doesn't drop the cached mic grant. Once Chrome (or Safari) has
// granted `getUserMedia`, every subsequent SR.start() rides the same grant.
//
// `lastInputMode` also lives here so that when the user mic-clicks on the
// EmptyHero composer (which unmounts the moment the first message is sent),
// the bottom composer that remounts can still see "the previous turn was
// voice" and arm conversation-mode reactivation.
function _g() {
    if (typeof window === "undefined") return {};
    if (!window.__tcVoice)
        window.__tcVoice = {
            stream: null,
            rec: null,
            recorder: null,
            lastInputMode: "typed",
        };
    return window.__tcVoice;
}

async function ensureMicStream() {
    const g = _g();
    if (g.stream && g.stream.active) return g.stream;
    if (!navigator.mediaDevices?.getUserMedia) return null;
    g.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    return g.stream;
}

function ensureRecognizer() {
    const g = _g();
    if (g.rec) return g.rec;
    g.rec = new SR();
    g.rec.continuous = false;
    g.rec.interimResults = true;
    return g.rec;
}

/**
 * Hands-free composer.
 *
 *  - Native SR auto-submits when `onend` fires after at least one final
 *    transcript (unless the user typed in the input meanwhile).
 *  - Server-fallback path (Safari Dravidian) uses MediaRecorder → POST
 *    to `/api/speech/transcribe`, then auto-submits the same way.
 *  - After every assistant reply (signalled via `assistantReplyTick`), if
 *    the previous turn was voice AND conversation mode is on, the mic
 *    re-activates for a 10 s listening window with a calm ripple wave.
 *  - User typing during a listening window cancels listening immediately
 *    so the manual flow always wins.
 */
export default function ChatComposer({
    disabled,
    onSend,
    onType,
    assistantReplyTick = 0,
}) {
    const [value, setValue] = useState("");
    const [locating, setLocating] = useState(false);
    const [recording, setRecording] = useState(false);
    const [convActive, setConvActive] = useState(false); // true while in the auto-listen window
    const [micError, setMicError] = useState(false);
    const [voiceTipDismissed, setVoiceTipDismissed] = useState(
        () => safeStorage.getItem(VOICE_TIP_KEY) === "1",
    );
    const [conversationMode, setConversationMode] = useState(() => {
        // Phase 14.8.1 — `typeof localStorage` is NOT a safe probe under
        // Chrome / Brave / Safari incognito "Block all cookies / Block
        // all site data". In strict mode `localStorage` is a getter that
        // THROWS `SecurityError` on access, and `typeof` invokes the
        // getter (it's only safe against truly undeclared identifiers).
        // The throw propagates out of the useState initializer and kills
        // React boot — the user sees a blank page (or the ErrorBoundary
        // fallback at best). Use `safeStorage.isPersistent` which was
        // probed once at module load inside a try/catch and never throws.
        if (!safeStorage.isPersistent) return true;
        const v = safeStorage.getItem(CONVERSATION_MODE_KEY);
        return v === null ? true : v === "1";
    });
    const { userGeo, setUserGeo } = useGeo();
    const { t, i18n } = useTranslation();

    const inputRef = useRef(null);
    const baseRef = useRef("");
    // `lastInputMode` is read out of the window-scoped voice cache so the
    // bottom composer (which mounts the moment EmptyHero unmounts after the
    // first send) can still see that the previous turn was voice.
    const userEditedRef = useRef(false); // set on input/keydown after onend until next round
    const gotFinalRef = useRef(false);
    const safetyTimerRef = useRef(null);
    const reactivateTimerRef = useRef(null);
    const valueRef = useRef("");
    valueRef.current = value;

    const clearSafetyTimer = useCallback(() => {
        if (safetyTimerRef.current) {
            clearTimeout(safetyTimerRef.current);
            safetyTimerRef.current = null;
        }
    }, []);
    const clearReactivateTimer = useCallback(() => {
        if (reactivateTimerRef.current) {
            clearTimeout(reactivateTimerRef.current);
            reactivateTimerRef.current = null;
        }
    }, []);

    const triggerMicError = useCallback(
        (msg, silent = false) => {
            setMicError(true);
            if (!silent) toast.error(msg);
            setTimeout(() => setMicError(false), 3000);
        },
        [],
    );

    const dismissVoiceTip = useCallback(() => {
        try {
            safeStorage.setItem(VOICE_TIP_KEY, "1");
        } catch {
            /* ignore */
        }
        setVoiceTipDismissed(true);
    }, []);

    // Phase 13A — listen for a global "prefill" event so cards (e.g. the
    // ItineraryCard "Modify" button) can drop text into the composer
    // without prop-drilling a setter ref through every consumer.
    useEffect(() => {
        function onPrefill(e) {
            const text = (e?.detail?.text || "").toString();
            if (!text) return;
            setValue(text);
            // Defer focus so the value lands first, cursor at end.
            setTimeout(() => {
                const el = inputRef.current;
                if (!el) return;
                try {
                    el.focus();
                    const len = el.value?.length ?? text.length;
                    el.setSelectionRange?.(len, len);
                } catch {
                    /* ignore */
                }
            }, 0);
        }
        if (typeof window !== "undefined") {
            window.addEventListener("tc:prefill-composer", onPrefill);
            return () => window.removeEventListener("tc:prefill-composer", onPrefill);
        }
        return undefined;
    }, []);

    /**
     * Submit helper. `mode` records whether the message originated from
     * the keyboard or the mic so the conversation-mode reactivation can
     * decide whether to auto-listen after the AI reply.
     */
    const submitWithMode = useCallback(
        (text, mode) => {
            const trimmed = (text || "").trim();
            if (!trimmed || disabled) return false;
            _g().lastInputMode = mode;
            userEditedRef.current = false;
            onType?.(false);
            if (!voiceTipDismissed) dismissVoiceTip();
            onSend(trimmed);
            setValue("");
            return true;
        },
        [disabled, onSend, onType, voiceTipDismissed, dismissVoiceTip],
    );

    function submit() {
        submitWithMode(valueRef.current, "typed");
    }

    function onKeyDown(e) {
        userEditedRef.current = true;
        // Any keystroke during a conversation-mode listen cancels listening.
        if (convActive) cancelListening();
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
        }
    }

    function handleChange(e) {
        const next = e.target.value;
        setValue(next);
        userEditedRef.current = true;
        if (convActive) cancelListening();
        if (onType) onType(Boolean(next.trim()));
    }

    function handleGps() {
        if (locating) return;
        if (!("geolocation" in navigator)) {
            toast.error("Geolocation isn't supported in this browser.");
            return;
        }
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                try {
                    const { latitude, longitude } = pos.coords;
                    const res = await axios.post(`${API}/reverse-geocode`, {
                        lat: latitude,
                        lon: longitude,
                    });
                    const data = res.data || {};
                    setUserGeo({
                        city: data.city,
                        country_code: data.country_code,
                        country_name: data.country_name,
                        nearest_iata: data.nearest_iata,
                        formatted_short: data.formatted_short,
                        lat: latitude,
                        lon: longitude,
                    });
                    toast.success(
                        data.formatted_short && data.formatted_short.startsWith("near ")
                            ? `Location set — ${data.formatted_short}`
                            : `Location set — Near ${data.city}${data.country_code ? ", " + data.country_code : ""}`,
                    );
                } catch (err) {
                    console.error("[gps] reverse-geocode failed", err);
                    toast.error("Couldn't resolve your location.");
                } finally {
                    setLocating(false);
                }
            },
            (err) => {
                console.warn("[gps] denied", err?.code);
                toast.error("Couldn't access your location. Falling back to IP-based location.");
                setLocating(false);
            },
            { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
        );
    }

    // ── Server-fallback recorder (Safari Dravidian path) ──────────────
    async function startServerFallback({ silent }) {
        const g = _g();
        try {
            await ensureMicStream();
        } catch (e) {
            const code = e?.name === "NotAllowedError" ? "not-allowed" : "audio-capture";
            if (!silent) {
                triggerMicError(
                    code === "not-allowed"
                        ? t("voice.permission_denied_toast")
                        : t("voice.unavailable_toast"),
                );
            }
            setRecording(false);
            setConvActive(false);
            return;
        }
        const mime = window.MediaRecorder?.isTypeSupported?.("audio/mp4")
            ? "audio/mp4"
            : "audio/webm";
        let recorder;
        try {
            recorder = new MediaRecorder(g.stream, { mimeType: mime });
        } catch {
            triggerMicError(t("voice.unavailable_toast"), silent);
            setRecording(false);
            setConvActive(false);
            return;
        }
        const chunks = [];
        recorder.ondataavailable = (ev) => {
            if (ev.data && ev.data.size) chunks.push(ev.data);
        };
        recorder.onstop = async () => {
            setRecording(false);
            const blob = new Blob(chunks, { type: mime });
            if (!blob.size) {
                setConvActive(false);
                return;
            }
            try {
                const fd = new FormData();
                fd.append("audio", blob, `voice.${mime.split("/")[1]}`);
                fd.append("lang", mapLocaleToBcp47(i18n.language));
                const r = await axios.post(`${API}/speech/transcribe`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                const text = (r.data?.transcript || "").trim();
                if (text && !userEditedRef.current) {
                    submitWithMode(text, "voice");
                } else if (text) {
                    setValue(text);
                }
            } catch (e) {
                if (!silent) {
                    triggerMicError(t("voice.server_transcribe_failed_toast"));
                }
            } finally {
                setConvActive(false);
                g.recorder = null;
            }
        };
        recorder.start();
        g.recorder = recorder;
        baseRef.current = value ? `${value.trimEnd()} ` : "";
        userEditedRef.current = false;
        setRecording(true);
        setMicError(false);
    }

    function stopServerFallback() {
        const g = _g();
        try {
            g.recorder?.stop();
        } catch {
            /* ignore */
        }
    }

    // ── Native SpeechRecognition path ─────────────────────────────────
    async function startNative({ silent }) {
        try {
            await ensureMicStream();
        } catch (e) {
            const code = e?.name === "NotAllowedError" ? "not-allowed" : "audio-capture";
            if (!silent) {
                triggerMicError(
                    code === "not-allowed"
                        ? t("voice.permission_denied_toast")
                        : t("voice.unavailable_toast"),
                );
            }
            setRecording(false);
            setConvActive(false);
            return;
        }
        const rec = ensureRecognizer();
        rec.lang = mapLocaleToBcp47(i18n.language);
        baseRef.current = value ? `${value.trimEnd()} ` : "";
        gotFinalRef.current = false;
        userEditedRef.current = false;

        rec.onstart = () => {
            setRecording(true);
            setMicError(false);
        };
        rec.onresult = (ev) => {
            let transcript = "";
            for (let i = 0; i < ev.results.length; i++) {
                transcript += ev.results[i][0].transcript;
                if (ev.results[i].isFinal) gotFinalRef.current = true;
            }
            const next = `${baseRef.current}${transcript}`;
            setValue(next);
            onType?.(Boolean(next.trim()));
        };
        rec.onerror = (ev) => {
            setRecording(false);
            const code = ev?.error;
            if (silent) {
                // In conversation-mode listening we never spam toasts;
                // the user hasn't actively asked us to listen this round.
                if (code === "service-not-allowed" || code === "language-not-supported") {
                    // Silent fall-through to server transcribe? Skip — just exit.
                }
                return;
            }
            if (code === "not-allowed" || code === "service-not-allowed") {
                if (shouldUseServerTranscribe(rec.lang)) {
                    triggerMicError(t("voice.lang_not_supported_in_browser") || t("voice.lang_unsupported_toast"));
                } else {
                    triggerMicError(t("voice.permission_denied_toast"));
                }
            } else if (code === "language-not-supported") {
                triggerMicError(t("voice.lang_not_supported_in_browser") || t("voice.lang_unsupported_toast"));
            } else if (code === "aborted" || code === "no-speech") {
                setMicError(false);
            } else {
                triggerMicError(t("voice.unavailable_toast"));
            }
        };
        rec.onend = () => {
            setRecording(false);
            clearSafetyTimer();
            const finalText = valueRef.current;
            if (gotFinalRef.current && !userEditedRef.current && finalText.trim()) {
                submitWithMode(finalText, "voice");
            } else {
                const el = inputRef.current;
                if (el) {
                    requestAnimationFrame(() => {
                        el.focus();
                        const len = el.value.length;
                        try {
                            el.setSelectionRange(len, len);
                        } catch {
                            /* ignore */
                        }
                    });
                }
            }
            setConvActive(false);
            if (!voiceTipDismissed) dismissVoiceTip();
        };

        try {
            rec.start();
        } catch {
            try {
                rec.abort();
                rec.start();
            } catch {
                triggerMicError(t("voice.unavailable_toast"), silent);
                setRecording(false);
                setConvActive(false);
            }
        }
    }

    function stopNative() {
        try {
            _g().rec?.stop();
        } catch {
            /* ignore */
        }
    }

    /** Cancel any in-flight listening — used when the user types during
     * the conversation-mode window. */
    const cancelListening = useCallback(() => {
        clearSafetyTimer();
        clearReactivateTimer();
        const lang = mapLocaleToBcp47(i18n.language);
        if (shouldUseServerTranscribe(lang)) {
            try {
                _g().recorder?.stop();
            } catch {
                /* ignore */
            }
        } else {
            try {
                _g().rec?.abort();
            } catch {
                /* ignore */
            }
        }
        setConvActive(false);
        setRecording(false);
    }, [clearSafetyTimer, clearReactivateTimer, i18n.language]);

    /**
     * Begin a listening round. `silent=true` means we entered via
     * conversation-mode auto-reactivation — errors stay quiet.
     */
    async function beginListening({ silent }) {
        if (!SR && !shouldUseServerTranscribe(mapLocaleToBcp47(i18n.language))) return;
        clearSafetyTimer();
        const lang = mapLocaleToBcp47(i18n.language);
        // 10 s safety timeout — if we never hear final speech, exit quietly.
        safetyTimerRef.current = setTimeout(() => {
            if (shouldUseServerTranscribe(lang)) stopServerFallback();
            else stopNative();
        }, 10_000);

        if (shouldUseServerTranscribe(lang)) {
            await startServerFallback({ silent });
        } else if (SR) {
            await startNative({ silent });
        }
    }

    function toggleRecording() {
        const lang = mapLocaleToBcp47(i18n.language);
        const useServer = shouldUseServerTranscribe(lang);
        if (recording || convActive) {
            // User clicked mid-listen — stop. If we have a buffered
            // transcript, the standard `onend`/`onstop` flow handles it.
            if (useServer) stopServerFallback();
            else stopNative();
            clearSafetyTimer();
            setConvActive(false);
            return;
        }
        beginListening({ silent: false });
    }

    // ── Conversation-mode reactivation: listen automatically after every
    // assistant reply IF the previous user turn was voice. ─────────────
    useEffect(() => {
        if (assistantReplyTick === 0) return undefined;
        if (!conversationMode) return undefined;
        if (_g().lastInputMode !== "voice") return undefined;
        clearReactivateTimer();
        reactivateTimerRef.current = setTimeout(() => {
            // Pause if the tab isn't visible — start fresh on next reply.
            if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
            setConvActive(true);
            beginListening({ silent: true });
        }, 600);
        return () => clearReactivateTimer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assistantReplyTick]);

    // Pause listening when the user backgrounds the tab.
    useEffect(() => {
        function onVis() {
            if (document.visibilityState !== "visible" && (recording || convActive)) {
                cancelListening();
            }
        }
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [recording, convActive, cancelListening]);

    // Best-effort cleanup on unmount.
    useEffect(() => {
        return () => {
            clearSafetyTimer();
            clearReactivateTimer();
            try {
                _g().rec?.abort();
            } catch {
                /* ignore */
            }
            try {
                _g().recorder?.stop();
            } catch {
                /* ignore */
            }
        };
    }, [clearSafetyTimer, clearReactivateTimer]);

    function toggleConversationMode() {
        const next = !conversationMode;
        setConversationMode(next);
        try {
            safeStorage.setItem(CONVERSATION_MODE_KEY, next ? "1" : "0");
        } catch {
            /* ignore */
        }
        if (!next) cancelListening();
    }

    const gpsActive = Boolean(userGeo);
    const lang = mapLocaleToBcp47(i18n.language);
    const voiceSupported = Boolean(SR) || shouldUseServerTranscribe(lang);
    const showVoiceTip = voiceSupported && !voiceTipDismissed;
    const showRipple = convActive && recording;

    return (
        <div data-testid="chat-composer-wrap">
            <div
                className="flex items-center gap-2 rounded-2xl border border-[#E5E5E5] bg-white px-3 py-2 shadow-[0_1px_3px_rgba(0,0,0,0.04)] focus-within:border-[#0A0A0A] focus-within:shadow-[0_1px_8px_rgba(0,0,0,0.08)]"
                data-testid="chat-composer"
            >
                {/* Phase 8M.1 — GPS button removed from the composer; the
                    location affordance now lives inside <NearMeChip /> above
                    the input (the whole chip is tappable). Frees up the
                    chat input's left edge. */}

                <input
                    ref={inputRef}
                    type="text"
                    value={value}
                    onChange={handleChange}
                    onKeyDown={onKeyDown}
                    disabled={disabled}
                    placeholder={t("chat.placeholder")}
                    data-testid="chat-input"
                    className="flex-1 bg-transparent px-1 py-2 text-[15px] outline-none placeholder:text-[#A3A3A3] disabled:opacity-60"
                />

                {voiceSupported && (
                    <button
                        type="button"
                        onClick={toggleConversationMode}
                        aria-label={t("voice.conversation_mode")}
                        title={
                            conversationMode
                                ? t("voice.conversation_mode")
                                : t("voice.conv_off_hint")
                        }
                        data-testid="conversation-toggle"
                        aria-pressed={conversationMode}
                        className={
                            "hidden h-9 w-9 shrink-0 items-center justify-center rounded-full transition sm:flex " +
                            (conversationMode
                                ? "text-[#0A0A0A] hover:bg-[#F5F5F5]"
                                : "text-[#A3A3A3] hover:bg-[#F5F5F5] hover:text-[#0A0A0A]")
                        }
                    >
                        <Sparkles size={15} strokeWidth={1.9} />
                    </button>
                )}

                {voiceSupported && (
                    <div className="relative flex h-9 w-9 shrink-0 items-center justify-center">
                        {recording && !showRipple && (
                            <span
                                aria-hidden
                                className="pulse-ring pointer-events-none absolute inset-0 rounded-full border-2 border-[#DC2626]"
                            />
                        )}
                        {showRipple && (
                            <>
                                <span
                                    aria-hidden
                                    className="tc-mic-wave pointer-events-none absolute inset-0 rounded-full border border-[#0A0A0A] motion-reduce:animate-none"
                                />
                                <span
                                    aria-hidden
                                    className="tc-mic-wave tc-mic-wave-delay pointer-events-none absolute inset-0 rounded-full border border-[#0A0A0A] motion-reduce:animate-none"
                                />
                            </>
                        )}
                        <button
                            type="button"
                            onClick={toggleRecording}
                            aria-label={recording ? t("voice.stop") : t("voice.start")}
                            aria-pressed={recording}
                            data-testid="mic-button"
                            className={
                                "relative flex h-9 w-9 items-center justify-center rounded-full transition " +
                                (recording
                                    ? "bg-[#DC2626] text-white"
                                    : "text-[#737373] hover:bg-[#F5F5F5] hover:text-[#0A0A0A]") +
                                (micError ? " mic-shake" : "")
                            }
                        >
                            <Mic
                                size={17}
                                strokeWidth={recording ? 2.4 : 1.8}
                                fill={recording ? "#fff" : "none"}
                            />
                        </button>
                    </div>
                )}

                <button
                    type="button"
                    onClick={submit}
                    disabled={disabled || !value.trim()}
                    aria-label={t("chat.send")}
                    data-testid="send-button"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0A0A0A] text-white transition hover:bg-[#262626] disabled:bg-[#E5E5E5] disabled:text-[#A3A3A3]"
                >
                    <ArrowUp size={18} strokeWidth={2.25} />
                </button>
            </div>

            {convActive && (
                <p
                    className="mt-1.5 text-center text-[11px] text-[#0A0A0A]"
                    data-testid="listening-hint"
                >
                    {t("voice.listening_hint")}
                </p>
            )}

            {showVoiceTip && !convActive && (
                <div
                    className="mt-1.5 flex items-center justify-center gap-2 text-[11px] text-[#9CA3AF]"
                    data-testid="voice-first-time-tip"
                >
                    <span>{t("voice.first_time_tip")}</span>
                    <button
                        type="button"
                        onClick={dismissVoiceTip}
                        aria-label="Dismiss"
                        data-testid="voice-tip-dismiss"
                        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[#9CA3AF] transition hover:bg-[#F5F5F5] hover:text-[#0A0A0A]"
                    >
                        <X size={10} strokeWidth={2.25} />
                    </button>
                </div>
            )}
        </div>
    );
}
